import React from "react";
import {
  Box,
  Typography,
  Link,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import {
  InsertDriveFileOutlined,
  Close as CloseIcon,
  BrokenImageOutlined,
} from "@mui/icons-material";
import { inferMediaType } from "../../helpers/inferMediaType";

interface IMediaItem {
  url: string;
  type?: string;
  caption?: string;
}

interface IMediaRendererProps {
  media: Array<IMediaItem>;
}

const extractFilename = (url: string): string => {
  return url.split("/").pop() || "file";
};

const MediaItemImage: React.FC<{ item: IMediaItem; onPreview: () => void }> = ({
  item,
  onPreview,
}) => {
  const [error, setError] = React.useState(false);

  if (error) {
    return <MediaErrorFallback url={item.url} />;
  }

  return (
    <Box>
      <Box
        component="img"
        src={item.url}
        alt={item.caption || "Image"}
        onError={() => setError(true)}
        onClick={onPreview}
        sx={{
          maxWidth: 200,
          maxHeight: 200,
          objectFit: "cover",
          borderRadius: 1,
          cursor: "pointer",
          display: "block",
          "&:hover": {
            opacity: 0.85,
          },
        }}
      />
      {item.caption && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {item.caption}
        </Typography>
      )}
    </Box>
  );
};

const MediaItemVideo: React.FC<{ item: IMediaItem }> = ({ item }) => {
  const [error, setError] = React.useState(false);

  if (error) {
    return <MediaErrorFallback url={item.url} />;
  }

  return (
    <Box>
      <Box
        component="video"
        controls
        onError={() => setError(true)}
        sx={{
          maxWidth: 300,
          borderRadius: 1,
          display: "block",
        }}
      >
        <source src={item.url} />
      </Box>
      {item.caption && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {item.caption}
        </Typography>
      )}
    </Box>
  );
};

const MediaItemAudio: React.FC<{ item: IMediaItem }> = ({ item }) => {
  const [error, setError] = React.useState(false);

  if (error) {
    return <MediaErrorFallback url={item.url} />;
  }

  return (
    <Box>
      <Box
        component="audio"
        controls
        onError={() => setError(true)}
        sx={{ display: "block" }}
      >
        <source src={item.url} />
      </Box>
      {item.caption && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {item.caption}
        </Typography>
      )}
    </Box>
  );
};

const MediaItemDocument: React.FC<{ item: IMediaItem }> = ({ item }) => {
  const filename = extractFilename(item.url);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <InsertDriveFileOutlined fontSize="small" sx={{ color: "primary.main" }} />
        <Link
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          variant="body2"
        >
          {filename}
        </Link>
      </Box>
      {item.caption && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {item.caption}
        </Typography>
      )}
    </Box>
  );
};

const MediaErrorFallback: React.FC<{ url: string }> = ({ url }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        bgcolor: "grey.100",
        borderRadius: 1,
      }}
    >
      <BrokenImageOutlined fontSize="small" sx={{ color: "text.disabled" }} />
      <Typography variant="caption" color="text.secondary">
        Media unavailable
      </Typography>
      <Link
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        variant="caption"
        sx={{ ml: 0.5 }}
      >
        {url}
      </Link>
    </Box>
  );
};

export const MediaRenderer: React.FC<IMediaRendererProps> = ({ media }) => {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  const items = media.slice(0, 10);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {items.map((item, index) => {
          const mediaType = inferMediaType(item.url, item.type);

          switch (mediaType) {
            case "image":
              return (
                <MediaItemImage
                  key={index}
                  item={item}
                  onPreview={() => setPreviewUrl(item.url)}
                />
              );
            case "video":
              return <MediaItemVideo key={index} item={item} />;
            case "audio":
              return <MediaItemAudio key={index} item={item} />;
            case "document":
              return <MediaItemDocument key={index} item={item} />;
            default:
              return <MediaItemDocument key={index} item={item} />;
          }
        })}
      </Box>

      {/* Image Preview Modal */}
      <Dialog
        open={Boolean(previewUrl)}
        onClose={() => setPreviewUrl(null)}
        maxWidth="lg"
      >
        <IconButton
          onClick={() => setPreviewUrl(null)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "grey.500",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 1 }}>
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt="Preview"
              sx={{
                maxWidth: "100%",
                maxHeight: "80vh",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
